<script setup lang="ts">
import type { ApiMachineryGetById, SpecialPermissions } from '~/types'

export interface MachineryAccessoryInspectionDatePayload {
  id: string
  technicalInspectionDate?: Date | null
  securityInspectionDate?: Date | null
  specialPermissions: SpecialPermissions
}

const props = withDefaults(defineProps<{
  payload: ApiMachineryGetById | MachineryAccessoryInspectionDatePayload
  isMachineryAccessory?: boolean
}>(), {
  isMachineryAccessory: false,
})
const emit = defineEmits<{
  (e: 'saveMachinery', data: ApiMachineryGetById): void
  (e: 'saveMachineryAccessory', data: MachineryAccessoryInspectionDatePayload): void
}>()

const { useremail } = useAuthorization()

const { cloned: specialPermissions } = useCloned(props.payload.specialPermissions)

function submitForm(data: ApiMachineryGetById | MachineryAccessoryInspectionDatePayload) {
  // Machinery accessory update uses specific updateInspectionDate router that receives specific inspection date fields,
  // while machinery update uses the general update router that receives all fields.
  // Both require different types, which is why the data is asserted to the respective parameter types.

  if (props.isMachineryAccessory) {
    const machineryAccessoryData = {
      ...data,
      specialPermissions: specialPermissions.value,
      // Metadata for machinery accessory inspection date is updated directly in the router
    } as MachineryAccessoryInspectionDatePayload

    emit('saveMachineryAccessory', machineryAccessoryData)
  } else {
    const machineryData = {
      ...data,
      specialPermissions: specialPermissions.value,
      // As data is updated using the general update router, some data must be manually set from the client
      inspectionDateUpdatedAt: new Date(),
      inspectionDateUpdatedByEmail: useremail.value,
    } as ApiMachineryGetById

    emit('saveMachinery', machineryData)
  }
}
</script>

<template>
  <!-- @vue-expect-error: After upgrading to latest Vue 3.5 and TS 5.8 in #4136 inference became stricter, generating a new error for a working form, that had no explicit error before. To enable the upgrade we add this ignore: The TS-error was there before, it was just not caught. Now, its at least explicit and can be fixed in the future. TODO: Fix, improve or replace once we agree on a systematic fix -->
  <FormKit
    type="form"
    :value="payload"
    submit-label="Speichern"
    @submit="v => submitForm(v)"
  >
    <FormKitDate
      name="technicalInspectionDate"
      label="Nächste TÜV Prüfung fällig am"
    />

    <FormKitDate
      name="securityInspectionDate"
      label="Nächste Sicherheitsprüfung fällig am"
    />

    <FormKitDate
      v-if="!isMachineryAccessory"
      name="speedometerInspectionDate"
      label="Nächste Tacho Prüfung fällig am"
    />

    <MachinerySpecialPermissionInput
      v-model="specialPermissions"
    />
  </FormKit>
</template>
